import interpolateComponents from 'interpolate-components';
import React, { useState } from 'react';
import { sprintf } from 'sprintf-js';
import styled from 'styled-components';

import { CurrentUser } from 'api/currentUser';
import { Org } from 'api/orgs';
import { FilestackImageData } from 'api/shared';
import Box from 'components/Box';
import BulletList from 'components/BulletList';
import {
  CloseButton,
  PrimaryButtonLink,
  PrimaryButtonRouterLink,
  SecondaryButton,
  SecondaryButtonRouterLink,
} from 'components/Button';
import Container from 'components/Container';
import Form, { ImagePickerField, LogoPickerField } from 'components/Form';
import Grid, { GridItem } from 'components/Grid';
import Hide from 'components/Hide';
import BaseIcon from 'components/Icon';
import { LogoImage } from 'components/Image';
import InfoList from 'components/InfoList';
import Link from 'components/Link';
import ListingSignupCta from 'components/ListingSignupCta';
import PageToolbar from 'components/PageToolbar';
import PrintHidden from 'components/PrintHidden';
import SaveButton from 'components/SaveButton';
import ShareListingButton from 'components/ShareListingButton';
import Text, { BodySmall, H2 } from 'components/Text';
import TrackedLink from 'components/TrackedLink';
import { TruncatedHtmlWithLinks } from 'components/TruncatedHtml';
import TruncatedListings from 'components/TruncatedListings';
import AdminRequestContainer from 'containers/AdminRequestContainer';
import ClientOnly from 'containers/ClientOnly';
import FlagListingContainer from 'containers/FlagListingContainer';
import MediaGalleryContainer from 'containers/MediaGalleryContainer';
import NoAmp from 'containers/NoAmp';
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line import/namespace, import/default
import SaveListingContainer from 'containers/SaveListingContainer';
import { SearchContext } from 'containers/SearchContextHeaderContainer';
import ListingPage, { Section, SectionTitle } from 'pages/ListingPage';
import { getCityStateString } from 'utils/address';
import { trackClicked } from 'utils/analytics';
import { monthAndYear, shorterDate } from 'utils/date';
import { imageUrl } from 'utils/filestack';
import { isEnabled as daysEnabled } from 'utils/idealistdayevents';
import * as localStorage from 'utils/localStorage';
import { getRgbaString, retinaBackgroundImage } from 'utils/style';

import { breakpoints, colors } from '../theme';

const userOwnsOrg = (user: CurrentUser, org: Org): boolean =>
  user && user.orgs && user.orgs.some((o) => o.id === org.id);

const contactLink = (
  <TrackedLink
    href="https://www.idealist.org/en/about/contact"
    target="_blank"
    eventName="Org Preview - Contact Idealist"
    color="white"
  />
);
const ApplyButtonContainer = styled(({ className, children }) => (
  <Box className={className}>{children}</Box>
))`
  flex-grow: 0.25;
  margin: 4px;
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const OrgPreviewInfo = ({ org }: { org: Org }) => {
  const createdDate = shorterDate(org.created);
  const submittedToday = createdDate === shorterDate(Date.now());
  const previouslyDismissed =
    localStorage.getItem('orgPreviewInfoDismissed') || {};
  const [dismissed, setDismissed] = useState(previouslyDismissed[org.id]);

  const closeInfo = () => {
    const dismissedList = localStorage.getItem('orgPreviewInfoDismissed') || {};
    localStorage.setItem('orgPreviewInfoDismissed', {
      ...dismissedList,
      [org.id]: true,
    });
    setDismissed(true);
  };

  return dismissed ? null : (
    <Box
      bg={`rgba(${getRgbaString(colors.waterBlue, 0.1)})`}
      py={40}
      position="relative"
    >
      <Box position="absolute" top={12} right="4px">
        <CloseButton onClick={closeInfo} />
      </Box>
      <Grid>
        <GridItem columnStart={[1, 1, 1, 1]} columnEnd={[13, 13, 13, 13]}>
          <Box pr={20}>
            <BodySmall>
              {submittedToday ? (
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <>
                  {org.is501c3
                    ? interpolateComponents({
                        mixedString: getText(
                          'Thank you! Your organization has been submitted for approval and will be reviewed within two business days. In the meantime, you can customize your profile, create a job listing, or contact the {{link}}Idealist Community Support Team{{/link}} if you have any questions.'
                        ),
                        components: {
                          link: contactLink,
                        },
                      })
                    : interpolateComponents({
                        mixedString: getText(
                          'Thank you! Your organization has been submitted for approval and will be reviewed within two business days. In the meantime, you can customize your profile or contact the {{link}}Idealist Community Support Team{{/link}} if you have any questions.'
                        ),
                        components: {
                          link: contactLink,
                        },
                      })}
                </>
              ) : (
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <>
                  {org.is501c3
                    ? interpolateComponents({
                        mixedString: sprintf(
                          getText(
                            'Your organization was submitted for approval on %(createdDate)s. In the meantime, you can customize your profile, create a job listing, or contact the {{link}}Idealist Community Support Team{{/link}} if you have any questions or if it has been more than two business days since applying.'
                          ),
                          {
                            createdDate,
                          }
                        ),
                        components: {
                          link: contactLink,
                          createdDate,
                        },
                      })
                    : interpolateComponents({
                        mixedString: sprintf(
                          getText(
                            'Your organization was submitted for approval on %(createdDate)s. In the meantime, you can customize your profile, or contact the {{link}}Idealist Community Support Team{{/link}} if you have any questions or if it has been more than two business days since applying.'
                          ),
                          {
                            createdDate,
                          }
                        ),
                        components: {
                          link: contactLink,
                          createdDate,
                        },
                      })}
                </>
              )}
            </BodySmall>
          </Box>
        </GridItem>
      </Grid>
    </Box>
  );
};

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const Icon = (props) => (
  <NoAmp>
    {/* TODO: Fix this the next time the file is edited. */}
    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
    <BaseIcon {...props} />
  </NoAmp>
);

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const ActionButtons = ({
  org,
  isPublished,
  editMode,
  setEditMode,
}: {
  org: Org;
  isPublished?: boolean;
  editMode: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setEditMode: (...args: Array<any>) => any;
}) => {
  const editUrl = `/admin/${org.id}/edit`;
  const EditButton = org.isFrozen
    ? SecondaryButtonRouterLink
    : PrimaryButtonRouterLink;

  const toggleEditMode = () => {
    trackClicked(`Org Profile: Admin ${editMode ? 'Preview' : 'Edit'} Mode`);
    setEditMode(!editMode);
  };

  return (
    <>
      {org.isFrozen && (
        <PrimaryButtonLink
          href="https://www.idealist.org/en/about/contact"
          size="small"
          reverse
        >
          {getText('Contact')}
        </PrimaryButtonLink>
      )}
      <EditButton
        to={editUrl}
        data-qa-id="edit-button"
        size="small"
        reverse
        onClick={() => {
          if (!org.isFrozen && !isPublished) {
            trackClicked('Org Preview - Edit Org Profile');
          }
        }}
      >
        {getText('Edit')}
      </EditButton>
      <SecondaryButton
        size="small"
        reverse
        onClick={toggleEditMode}
        data-qa-id="preview-button"
      >
        {/* TODO: Fix this the next time the file is edited. */}
        {/* eslint-disable-next-line no-nested-ternary */}
        {!editMode ? (
          getText('View Edit Mode')
        ) : isPublished ? (
          <>
            <Hide sm md lg xl>
              {getText('Preview')}
            </Hide>
            <Hide xs>{getText('View Public Profile')}</Hide>
          </>
        ) : (
          getText('Preview')
        )}
      </SecondaryButton>
      {!org.isFrozen && (org.is501c3 || isPublished) && (
        <SecondaryButtonRouterLink
          size="small"
          reverse
          to={`/admin/${org.id}/jobs/create`}
          onClick={() => {
            if (isPublished) {
              trackClicked('Org Profile - Post a Job');
            } else {
              trackClicked('Org Preview - Post a Job');
            }
          }}
        >
          {getText('Post a Job')}
        </SecondaryButtonRouterLink>
      )}
    </>
  );
};

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const Toolbar = ({
  org,
  isPublished,
  editMode,
  setEditMode,
}: {
  org: Org;
  isPublished?: boolean;
  editMode: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setEditMode: (...args: Array<any>) => any;
}) => (
  <PageToolbar
    centered
    message={
      org.isFrozen
        ? getText(
            'Your organization has been frozen. Please contact the Idealist Community Support Team to resolve this issue.'
          )
        : getText('You are an administrator of this organization')
    }
    tools={
      <ActionButtons
        org={org}
        isPublished={isPublished}
        editMode={editMode}
        setEditMode={setEditMode}
      />
    }
  />
);

const headerImageUrl = ({
  handle,
  ratio,
  width,
  height,
  mimetype,
}: {
  handle: string;
  ratio: number;
  width: number;
  height: number;
  mimetype: string;
}) =>
  imageUrl({
    handle,
    mimetype,
    transform: `resize=width:${width * ratio},height:${
      height * ratio
    },fit:crop/quality=value:70${
      mimetype === 'image/tiff' ? '/output=format:jpg' : ''
    }`,
  });

const Banner = styled(
  ({ handle, mimetype, ...props }: { handle: string; mimetype: string }) => (
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div {...props} data-qa-id="banner-image" />
  )
)`
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-position: top center;
  background-size: cover;
  ${(props) =>
    retinaBackgroundImage(
      headerImageUrl({
        handle: props.handle,
        ratio: 1,
        width: 320,
        height: 35,
        mimetype: props.mimetype,
      }),
      {
        filename2x: headerImageUrl({
          handle: props.handle,
          ratio: 2,
          width: 320,
          height: 35,
          mimetype: props.mimetype,
        }),
        filename3x: headerImageUrl({
          handle: props.handle,
          ratio: 3,
          width: 320,
          height: 35,
          mimetype: props.mimetype,
        }),
      }
    )};
  @media (min-width: ${breakpoints[2]}) {
    ${(props) =>
      retinaBackgroundImage(
        headerImageUrl({
          handle: props.handle,
          ratio: 1,
          width: 1440,
          height: 200,
          mimetype: props.mimetype,
        }),
        {
          filename2x: headerImageUrl({
            handle: props.handle,
            ratio: 2,
            width: 1440,
            height: 200,
            mimetype: props.mimetype,
          }),
          filename3x: headerImageUrl({
            handle: props.handle,
            ratio: 3,
            width: 1440,
            height: 200,
            mimetype: props.mimetype,
          }),
        }
      )};
  }
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const OrgPage = ({
  org,
  areasOfFocus,
  searchContext,
  currentUser,
  isPreview,
  isPublished,
  onSubmit,
}: {
  org: Org;
  areasOfFocus?: Array<string> | null | undefined;
  searchContext: SearchContext | null | undefined;
  currentUser: CurrentUser;
  isPreview?: boolean;
  isPublished?: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit?: (...args: Array<any>) => any;
}) => {
  const [editMode, setEditMode] = useState(true);
  const jobCount = org.jobs ? org.jobs.length : 0;
  const internshipCount = org.internships ? org.internships.length : 0;
  const volopCount = org.volops ? org.volops.length : 0;
  const hasListings = jobCount > 0 || internshipCount > 0 || volopCount > 0;
  const shareListingButton = (
    <ShareListingButton listingType="ORG" listing={org} disabled={isPreview} />
  );
  const listingAddressString = org.address && (
    <>{getCityStateString(org.address)}</>
  );
  const orgOwner = userOwnsOrg(currentUser, org);
  const areas = areasOfFocus || org.areasOfFocus;
  const confirmModal = {
    type: 'danger',
    title: getText('Remove Image'),
    buttonText: getText('Remove'),
    body: getText('Are you sure you want to remove this image?'),
  };

  const onImageChange = (
    name,
    image: FilestackImageData | null | undefined,
    updateValue,
    submitForm
  ) => {
    trackClicked(`Org profile: upload ${name}`);
    updateValue(name, image, submitForm);
  };

  const submitHandler = (props: {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    values: Record<string, any>;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleError: (...args: Array<any>) => any;
  }) => {
    if (onSubmit) {
      onSubmit(props);
    }
  };

  return (
    <ListingPage
      listingType="ORG"
      orgType={org.orgType}
      searchContext={searchContext}
      url={org.url}
      title={org.name}
      subtitle={
        <>
          {listingAddressString}
          {org.links.length > 0 &&
            org.links.map((link) => (
              <React.Fragment key={link}>
                {' | '}
                <Link href={link} target="_blank" rel="noopener noreferrer">
                  {link}
                </Link>
              </React.Fragment>
            ))}
          <PrintHidden>
            <Hide lg xl>
              <Container mt="25px">{shareListingButton}</Container>
            </Hide>
          </PrintHidden>
        </>
      }
      toolbar={
        orgOwner && (
          <Toolbar
            org={org}
            isPublished={isPublished}
            editMode={editMode}
            setEditMode={setEditMode}
          />
        )
      }
      isAdmin={orgOwner}
      renderActionButtons={(mobile) => (
        <Box classes={['flex', 'align-center']} flexWrap="wrap" m={-1}>
          {hasListings && (
            <ApplyButtonContainer>
              <PrimaryButtonLink href="#opportunities" width="100%">
                {getText('See opportunities')}
              </PrimaryButtonLink>
            </ApplyButtonContainer>
          )}
          <NoAmp>
            <>
              <SaveListingContainer
                listingId={org.id}
                listingType="ORG"
                listingName={org.name}
                orgId={org.id}
                orgType={org.orgType}
                render={({ saveListing, unsaveListing, saved, canSave }) => (
                  <SaveButton
                    m={1}
                    saved={saved}
                    disabled={!canSave || isPreview}
                    onClick={saved ? unsaveListing : saveListing}
                    data-qa-id={saved ? 'unsave-listing' : 'save-listing'}
                  >
                    {getText('Save')}
                  </SaveButton>
                )}
              />
              {!mobile && <Box m={1}>{shareListingButton}</Box>}
            </>
          </NoAmp>
        </Box>
      )}
      details={
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          <Container px={0} mb={2}>
            <Container px={0} pb={3} id="about">
              <SectionTitle mb="11px">{getText('About Us')}</SectionTitle>
              <TruncatedHtmlWithLinks
                html={org.description}
                truncatedHtml={org.descriptionTruncated}
                showMoreLinkTitle={getText('Read more about this organization')}
                showLessLinkTitle={getText('Read less about this organization')}
                renderHiddenLink={
                  !isPreview
                    ? () => (
                        <FlagListingContainer
                          listingId={org.id}
                          listingType="ORG"
                          render={({ flagListing }) => (
                            // TODO: Fix this the next time the file is edited.
                            // eslint-disable-next-line jsx-a11y/anchor-is-valid
                            <Link
                              href="#"
                              data-qa-id="flag-content"
                              onClick={(e) => {
                                e.preventDefault();
                                flagListing();
                              }}
                            >
                              {getText('Report this organization')}
                            </Link>
                          )}
                        />
                      )
                    : undefined
                }
              />
            </Container>
            {areas.length > 0 && (
              <Section title={getText('Issue Areas Include')}>
                <BulletList
                  columns={2}
                  itemMargin={32}
                  items={areas.map((area) => ({
                    key: area,
                    value: area,
                  }))}
                />
              </Section>
            )}
            <Section
              title={
                org.phone || org.email
                  ? getText('Location & Contact')
                  : getText('Location')
              }
            >
              <InfoList columns={1}>
                {org.phone && (
                  <li>
                    <Icon name="phone" />
                    <TrackedLink
                      href={`tel:${org.phone.replace(/[^0-9+]/g, '')}`}
                      eventName="Organization phone number"
                      eventProperties={{
                        method: 'Phone',
                      }}
                    >
                      {org.phone}
                    </TrackedLink>
                  </li>
                )}
                {org.email && (
                  <li>
                    <Icon name="mail" />
                    <TrackedLink
                      href={`mailto:${org.email}`}
                      target="_blank"
                      eventName="Organization email"
                      eventProperties={{
                        method: 'Email',
                      }}
                    >
                      <Box whiteSpace="nowrap">{org.email}</Box>
                    </TrackedLink>
                  </li>
                )}
                {org.address && (
                  <li>
                    <Icon name="location" />
                    <Box>
                      {org.address.full}
                      {org.address.line2 && <br />}
                      {org.address.line2}
                    </Box>
                  </li>
                )}
              </InfoList>
            </Section>
            {org.images.length > 0 && (
              <Section
                title={sprintf(getText('Photos for %(orgName)s'), {
                  orgName: org.name,
                })}
              >
                <MediaGalleryContainer items={org.images} />
              </Section>
            )}
            {hasListings && (
              <Section
                title={sprintf(getText('Current listings by %(orgName)s'), {
                  orgName: org.name,
                })}
                // @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: Element[]; title: any; id: strin... Remove this comment to see the full error message
                id="opportunities"
              >
                {org.jobs.length > 0 && (
                  <Container mb="32px" id="jobs">
                    <TruncatedListings listingType="JOB" listings={org.jobs} />
                  </Container>
                )}
                {org.internships.length > 0 && (
                  <Container mb="32px" id="internships">
                    <TruncatedListings
                      listingType="INTERNSHIP"
                      listings={org.internships}
                    />
                  </Container>
                )}
                {org.volops.length > 0 && (
                  <Container mb="32px" id="volops">
                    <TruncatedListings
                      listingType="VOLOP"
                      listings={org.volops}
                    />
                  </Container>
                )}
                <ClientOnly>
                  {daysEnabled() && org.idealistDayEvents.length > 0 && (
                    <Container mb="32px" id="events">
                      <TruncatedListings
                        listingType="IDEALIST_DAY_EVENT"
                        listings={org.idealistDayEvents}
                      />
                    </Container>
                  )}
                </ClientOnly>
              </Section>
            )}
          </Container>
        </>
      }
      banner={
        ((orgOwner && editMode) || org.banner) && (
          <>
            {isPreview && !isPublished && !org.isFrozen && (
              <OrgPreviewInfo org={org} />
            )}
            {org.orgType !== 'CORPORATION' && (
              <Box height={[userOwnsOrg ? 80 : 35, null, null, 200]}>
                {orgOwner && editMode ? (
                  <Form
                    initialValues={{
                      banner: org.banner,
                      id: org.id,
                    }}
                    // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
                    onSubmit={submitHandler}
                    render={({ updateValue, submitForm }) => (
                      <ImagePickerField
                        name="banner"
                        format="banner"
                        message={getText('Drag a banner image here')}
                        aspectRatio={7.2}
                        altText={org.name}
                        overlayButton
                        onChange={(image) =>
                          onImageChange(
                            'banner',
                            image,
                            updateValue,
                            submitForm
                          )
                        }
                        confirmModal={confirmModal}
                        extraAnalyticsData={{
                          context: 'Org Page',
                          file_type: 'Org Banner',
                          file_label: 'Drag a banner image here',
                        }}
                      />
                    )}
                  />
                ) : (
                  org.banner && (
                    <Banner
                      handle={org.banner.handle}
                      mimetype={org.banner.mimetype}
                    />
                  )
                )}
              </Box>
            )}
          </>
        )
      }
      sidebarImage={
        orgOwner && editMode ? (
          <Box mb={-24}>
            <Form
              initialValues={{
                logo: org.logo,
                id: org.id,
              }}
              // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
              onSubmit={submitHandler}
              render={({ updateValue, submitForm }) => (
                <LogoPickerField
                  name="logo"
                  altText={org.name}
                  onChange={(image) =>
                    onImageChange('logo', image, updateValue, submitForm)
                  }
                  confirmModal={confirmModal}
                  extraAnalyticsData={{
                    context: 'Org Page',
                    file_type: 'Org Logo',
                    file_label: 'Drag a logo here',
                  }}
                />
              )}
            />
          </Box>
        ) : (
          org.logo && (
            <NoAmp>
              <LogoImage
                data-qa-id="org-logo"
                justifyContent="flex-start"
                fsWidth={180}
                fsHeight={128}
                handle={org.logo ? org.logo.handle : undefined}
                mimetype={org.logo ? org.logo.mimetype : undefined}
                alt={sprintf(getText('Logo of %(orgName)s'), {
                  orgName: org.name,
                })}
              />
            </NoAmp>
          )
        )
      }
      sidebarTitle={
        <Box mb="24px" display="flex" flexDirection="column">
          <H2 styleAs="h3" m="0">
            {org.name}
          </H2>
          <Box my="4px">{listingAddressString}</Box>
        </Box>
      }
      sidebarHeader={
        <Text
          display={['inline-block', 'inline-block', 'inline-block', 'block']}
          color="battleshipGrey"
        >
          {sprintf(getText('Joined in %(monthAndYear)s'), {
            monthAndYear: monthAndYear(org.created),
          })}
        </Text>
      }
      sidebarButtons={
        <>
          {hasListings && (
            <Box>
              <PrimaryButtonLink m={1} href="#opportunities" width="100%">
                {getText('See opportunities')}
              </PrimaryButtonLink>
            </Box>
          )}
          <NoAmp>
            <Hide xs sm>
              <Box display="flex" mb="40px" alignItems="center">
                <SaveListingContainer
                  listingId={org.id}
                  listingType="ORG"
                  listingName={org.name}
                  orgId={org.id}
                  orgType={org.orgType}
                  render={({ saveListing, unsaveListing, saved, canSave }) => (
                    <Box m="0 4px 4px 0" width="100%">
                      <SaveButton
                        m={1}
                        width="100%"
                        saved={saved}
                        disabled={!canSave || isPreview}
                        onClick={saved ? unsaveListing : saveListing}
                        data-qa-id={saved ? 'unsave-listing' : 'save-listing'}
                      >
                        {getText('Save')}
                      </SaveButton>
                    </Box>
                  )}
                />
                <Box m="0 0 4px 4px" width="100%">
                  <ShareListingButton
                    disabled={isPreview}
                    listing={org}
                    listingType="ORG"
                    width="100%"
                  />
                </Box>
              </Box>
            </Hide>
          </NoAmp>
        </>
      }
      sidebarLinks={
        <>
          {jobCount > 0 && (
            <Box mb={3}>
              <Link href="#jobs">
                {sprintf(
                  jobCount > 1
                    ? getText('%(jobCount)s current jobs')
                    : getText('%(jobCount)s current job'),
                  {
                    jobCount,
                  }
                )}
              </Link>
            </Box>
          )}
          {internshipCount > 0 && (
            <Box mb={3}>
              <Link href="#internships">
                {sprintf(
                  internshipCount > 1
                    ? getText('%(internshipCount)s current internships')
                    : getText('%(internshipCount)s current internship'),
                  {
                    internshipCount,
                  }
                )}
              </Link>
            </Box>
          )}
          {volopCount > 0 && (
            <Box mb={3}>
              <Link href="#volops">
                {sprintf(
                  volopCount > 1
                    ? getText('%(volopCount)s current volunteer opportunities')
                    : getText('%(volopCount)s current volunteer opportunity'),
                  {
                    volopCount,
                  }
                )}
              </Link>
            </Box>
          )}
          <ClientOnly>
            <AdminRequestContainer
              objectId={org.id}
              objectType="ORG"
              objectName={org.name}
              render={({ requestAdmin }) => (
                <Text pb={3} color="battleshipGrey">
                  {sprintf(getText('Do you work for %(orgName)s?'), {
                    orgName: org.name,
                  })}{' '}
                  {/* TODO: Fix this the next time the file is edited. */}
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <Link
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      requestAdmin();
                    }}
                  >
                    {getText('Become an administrator')}
                  </Link>
                </Text>
              )}
            />
          </ClientOnly>
        </>
      }
      footer={
        !isPreview &&
        currentUser &&
        !currentUser.id && (
          <ListingSignupCta listingUrl={org.url[USER_LOCALE]} type="ORG" />
        )
      }
    />
  );
};

export default OrgPage;
